import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, View } from 'react-native';

import {
	ComposerAttachments,
	NO_ATTACHABLE,
	totalAttachmentBytes,
	type ComposerAttachmentItem,
} from '@/components/composer/composer-attachments';
import { ActivityIndicator, Button, ComposerWindow, Text, TextInput, useTheme } from '@/components/ui';
import { useComposerDraft } from '@/hooks/use-composer-draft';
import { useForwardMessage, useForwardPreview, useUsers } from '@/hooks/use-data';
import type { ForwardResult } from '@/services/data-service';
import { formatDocumentSize } from '@/utils/document-download';

/**
 * Hand this email to someone who has to act on it elsewhere (#466).
 *
 * Modelled on `components/applications/needs-info-modal.tsx`, and for the same reason its
 * docblock gives: a gated skip must never read as a success. Outside production the send
 * is simulated, so the banner says so while the sender is still typing rather than leaving
 * them to infer it from a toast after the fact.
 *
 * The attachment note is the other thing that has to be said UP FRONT. Files are filed
 * into Dropbox and referenced by a viewer URL that grants no access of its own — useful to
 * a colleague, useless to an outside vendor — and someone forwarding a fence quote to a
 * contractor needs to know that before they press Send, not after the contractor replies
 * asking for the file.
 */

export interface ForwardModalProps {
	messageId: string;
	visible: boolean;
	onDismiss: () => void;
	/** Fired after a send so the parent can report the outcome honestly. */
	onSent: (result: ForwardResult) => void;
	onError: (error: unknown, fallback: string) => void;
	/** Open collapsed — used when `ComposerHost` restores a draft after a page reload. */
	startMinimized?: boolean;
	/** Bumped by the host to bring an already-open, collapsed composer back to the front. */
	expandRequest?: number;
	/** Lets the host warn before a click replaces or closes work in progress. */
	onDirtyChange?: (dirty: boolean) => void;
	/** The signed-in user id, from `ComposerHost`. Absent means do not persist a draft. */
	draftScope?: string | null;
}

/** Only the sender's own words; the recipient list and attachments re-derive from the preview. */
interface ForwardDraft {
	to: string;
	note: string;
	includeAttachments: boolean;
}

export const ForwardModal: React.FC<ForwardModalProps> = ({
	messageId, visible, onDismiss, onSent, onError, startMinimized, expandRequest, onDirtyChange, draftScope,
}) => {
	const theme = useTheme();
	const { data: preview, isLoading, error } = useForwardPreview(messageId, { enabled: visible });
	const forward = useForwardMessage();
	const { data: userPage } = useUsers({ limit: 500 });

	const [to, setTo] = useState('');
	const [note, setNote] = useState('');
	const [includeAttachments, setIncludeAttachments] = useState(true);
	/**
	 * Files ADDED to the forward (#606) — sent as real attachments, unlike the message's own
	 * files above, which go as Dropbox links. Not kept in the draft: a staged upload is only
	 * worth re-offering while its bytes are still in staging, and re-picking is cheap.
	 */
	const [added, setAdded] = useState<ComposerAttachmentItem[]>([]);
	const [addedBusy, setAddedBusy] = useState(false);
	/**
	 * Has the sender actually touched this?
	 *
	 * NOT `to || note`, which looks equivalent and is not: the note is pre-seeded with the
	 * sender's signature, so a composer nobody has typed into is already non-empty. Using
	 * that as the signal would make a pristine forward claim unsaved work and prompt on
	 * every close.
	 */
	const [dirty, setDirty] = useState(false);
	/**
	 * Seeded ONCE per opening, not on every preview.
	 *
	 * `useForwardPreview` runs with `staleTime: 0`, so a window-focus refetch delivers a new
	 * preview object mid-compose. Re-seeding on that would wipe whatever the sender had
	 * typed — the failure the needs-info composer's `dirty` guard exists for. A ref is
	 * simpler here because there is nothing to re-seed FROM: the note is the sender's own
	 * words, not server-derived text.
	 */
	const noteSeeded = useRef(false);

	/**
	 * Start the note with the sender's signature, so it is visible and editable.
	 *
	 * The signature used to be appended server-side, which meant the recipient received a
	 * closing the sender never saw — and anyone who typed "Thanks, Lynn" themselves got it
	 * twice with no way to remove the automatic one. Putting it in the box is how the reply
	 * path already works, and makes deleting it just deleting text.
	 */
	useEffect(() => {
		if (!visible || noteSeeded.current || !preview) return;
		noteSeeded.current = true;
		if (preview.signature) setNote(`\n\n${preview.signature}`);
	}, [visible, preview]);

	// Reset per opening, so yesterday's recipient is not pre-filled on a different message.
	useEffect(() => {
		if (visible) return;
		setTo('');
		setNote('');
		setIncludeAttachments(true);
		setAdded([]);
		setDirty(false);
		noteSeeded.current = false;
		restoredRef.current = null;
	}, [visible]);

	/**
	 * Restore whatever was typed before the page was reloaded.
	 *
	 * `noteSeeded` is set too, so the signature seed above does not append a second copy on
	 * top of a note the sender already edited.
	 */
	const draft = useComposerDraft<ForwardDraft>(draftScope, visible ? `forward:${messageId}` : null);
	const restoredRef = useRef<string | null>(null);
	useEffect(() => {
		if (!visible || !draft.ready || !draft.restored) return;
		if (restoredRef.current === messageId) return;
		restoredRef.current = messageId;
		noteSeeded.current = true;
		setTo(draft.restored.to);
		setNote(draft.restored.note);
		setIncludeAttachments(draft.restored.includeAttachments);
		setDirty(true);
	}, [visible, messageId, draft.ready, draft.restored]);

	// Same signal the host uses to warn before a click throws this away.
	// A file still being staged is unsaved work too: closing mid-upload would drop it without a word.
	useEffect(() => { onDirtyChange?.(visible && (dirty || addedBusy)); }, [visible, dirty, addedBusy, onDirtyChange]);

	// Only once the sender has actually touched it — an untouched composer should not leave a
	// "draft" behind that is really just the seeded signature.
	useEffect(() => {
		if (!visible || !dirty) return;
		draft.save({ to, note, includeAttachments });
	}, [visible, dirty, to, note, includeAttachments, draft]);

	/** Colleagues, as one-tap recipients. Archived users are not people to hand work to. */
	const teammates = useMemo(() => {
		const rows = userPage?.data ?? [];
		return rows
			.filter((u) => !u.archivedAt && u.email)
			.slice(0, 8);
	}, [userPage?.data]);

	const gate = preview?.emailGate;
	const willSend = gate?.willActuallySend ?? false;
	const attachments = preview?.attachments ?? [];
	const busy = forward.isPending;

	// Mirrors the Send button's `disabled`. Cmd+Enter reaches `submit` directly, so without
	// the `busy` re-check two presses forward the message — and re-upload its attachments —
	// twice while the first request is still outstanding.
	const attachable = preview?.attachable ?? NO_ATTACHABLE;
	const addedFits = !added.length || totalAttachmentBytes(added) <= attachable.maxBytes;
	const canSubmit = !busy && !addedBusy && addedFits && !!to.trim();

	const submit = async () => {
		if (!canSubmit) return;
		try {
			const result = await forward.mutateAsync({
				id: messageId,
				body: { to: to.trim(), note, includeAttachments, attachments: added.map((item) => item.ref) },
			});
			// It is on the thread now; a stored draft would resurrect it next visit.
			draft.clear();
			onSent(result);
			onDismiss();
		} catch (e) {
			// Left open with the text intact — retyping a note because of a 400 is miserable.
			// The draft also stays in storage, so even a crash here does not lose it.
			onError(e, 'Could not forward this message.');
		}
	};

	/** Pinned to the window's bottom edge, so Send survives any amount of scrolling. */
	const actions = preview && !isLoading && !error ? (
		<View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 8 }}>
			{/* No backdrop-close on the composer window, so an explicit way out is required. */}
			<Button mode="outlined" onPress={onDismiss} disabled={busy}>Cancel</Button>
			<Button mode="contained" onPress={submit} disabled={!canSubmit} testID="btn-send-forward">
				{busy ? 'Sending…' : willSend ? 'Forward' : 'Record without emailing'}
			</Button>
		</View>
	) : undefined;

	return (
		<ComposerWindow
			visible={visible}
			onDismiss={onDismiss}
			onSubmit={submit}
			busy={busy}
			startMinimized={startMinimized}
			expandRequest={expandRequest}
			title="Forward this message"
			subtitle="Replies come back to you, not to the inbox."
			footer={actions}
			testID="forward-composer"
		>
			{isLoading ? (
				<View style={{ padding: 32, alignItems: 'center' }}><ActivityIndicator /></View>
			) : error || !preview ? (
				<Text style={{ color: theme.colors.error }}>Could not prepare this forward.</Text>
			) : (
				<>
						{/* Stated BEFORE sending. "Recorded but not emailed" is the normal outcome
							 outside production, and learning that from a toast is learning it too late. */}
						{!willSend ? (
							<View
								testID="forward-gate-banner"
								style={{
									flexDirection: 'row', alignItems: 'flex-start',
									backgroundColor: theme.colors.errorContainer,
									borderRadius: 8, padding: 10, marginBottom: 12,
								}}
							>
								<Ionicons name="warning" size={16} color={theme.colors.error} style={{ marginRight: 8, marginTop: 1 }} />
								<Text style={{ flex: 1, minWidth: 0, fontSize: 12, color: theme.colors.onErrorContainer }}>
									This will be recorded on the thread but NOT emailed. {gate?.reason}
								</Text>
							</View>
						) : null}

						<Text style={{ fontSize: 12, fontWeight: '600', marginBottom: 4 }}>To</Text>
							<TextInput
								value={to}
								onChangeText={(v) => { setTo(v); setDirty(true); }}
								placeholder="name@example.com, someone.else@example.com"
								autoCapitalize="none"
								testID="input-forward-to"
							/>

							{teammates.length > 0 ? (
								<View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
									{teammates.map((user) => (
										<Pressable
											key={user.id}
											onPress={() => {
												setTo((prev) => (prev.trim() ? `${prev.trim()}, ${user.email}` : user.email ?? ''));
												setDirty(true);
											}}
											testID={`forward-to-${user.id}`}
											style={{
												paddingVertical: 4, paddingHorizontal: 8, borderRadius: 12,
												borderWidth: 1, borderColor: theme.colors.outlineVariant ?? theme.colors.outline,
											}}
										>
											<Text style={{ fontSize: 11, color: theme.colors.onSurfaceVariant }}>{user.name || user.email}</Text>
										</Pressable>
									))}
								</View>
							) : null}

							<Text style={{ fontSize: 12, fontWeight: '600', marginTop: 14, marginBottom: 4 }}>
								Add a note (optional)
							</Text>
							<TextInput
								value={note}
								onChangeText={(v) => { setNote(v); setDirty(true); }}
								placeholder="Can you take a look at this?"
								multiline
								numberOfLines={4}
								testID="input-forward-note"
							/>

							{attachments.length > 0 ? (
								<View style={{ marginTop: 14 }} testID="forward-attachments">
									<Pressable
										onPress={() => { setIncludeAttachments((prev) => !prev); setDirty(true); }}
										testID="btn-toggle-forward-attachments"
										style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 6 }}
									>
										<Ionicons
											name={includeAttachments ? 'checkbox' : 'square-outline'}
											size={16}
											color={includeAttachments ? theme.colors.primary : theme.colors.onSurfaceVariant}
											style={{ marginRight: 8 }}
										/>
										<Text style={{ fontSize: 13, fontWeight: '600' }}>
											{`Include ${attachments.length} attachment${attachments.length === 1 ? '' : 's'}`}
										</Text>
									</Pressable>

									{includeAttachments ? (
										<View style={{ paddingLeft: 24 }}>
											{attachments.map((a) => (
												<Text key={a.id} style={{ fontSize: 11, color: theme.colors.onSurfaceVariant }}>
													{`• ${a.fileName}`}{a.fileSize ? ` (${formatDocumentSize(a.fileSize)})` : ''}
												</Text>
											))}
											{/*
												Said plainly, before the send. The files are FILED in Dropbox and
												linked by a viewer URL that confers no access — a colleague can
												open it, an outside contractor cannot, and the sender needs to
												know which of those they are dealing with.
											*/}
											<Text style={{ fontSize: 11, color: theme.colors.onSurfaceVariant, marginTop: 6 }}>
												{preview.dropboxTarget
													? `Filed in Dropbox under ${preview.dropboxTarget}. The links need a Dropbox account with access — `
														+ 'fine for staff, not for an outside vendor.'
													: 'Dropbox is not connected, so only the file names will be listed.'}
											</Text>
										</View>
									) : null}
								</View>
							) : null}

							<ComposerAttachments
								target={{ kind: 'message', id: messageId }}
								attachable={attachable}
								items={added}
								onChange={(update) => { setAdded(update); setDirty(true); }}
								onError={onError}
								onBusyChange={setAddedBusy}
								title="Attach files"
								disabled={busy}
							/>
					</>
			)}
		</ComposerWindow>
	);
};

export default ForwardModal;
